import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import {
  IMPORT_STAGES,
  type ImportStage,
  type ImportStageStatus,
} from "./externalRegisterImportModel";
import { EXTERNAL_REGISTER_SETTINGS_ROUTES } from "../externalRegisterRoutes";

export const ImportHeaderBar = ({
  draftIsActive,
  activeStage,
  stageStatuses,
  onCancel,
  onStageChange,
  resetAvailable,
  resetLabel,
}: {
  draftIsActive: boolean;
  activeStage: ImportStage;
  stageStatuses: Record<ImportStage, ImportStageStatus>;
  onCancel: () => void;
  onStageChange: (stage: ImportStage) => void;
  resetAvailable: boolean;
  resetLabel: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        pt: 3,
        backgroundColor: "background.default",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            sx={{ minWidth: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ fontWeight: 800 }}>
                {t("externalRegister.import.header.title")}
              </Typography>
              {draftIsActive && <Chip size="small" color="warning" label={t("externalRegister.import.header.draftSaved")} />}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(EXTERNAL_REGISTER_SETTINGS_ROUTES.importHistory)}
              >
                {t("externalRegister.import.header.buttons.importHistory")}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={onCancel}
                disabled={!resetAvailable}
              >
                {resetLabel}
              </Button>
            </Stack>
          </Stack>
          <WorkflowTabs
            activeStage={activeStage}
            statuses={stageStatuses}
            onChange={onStageChange}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

const WorkflowTabs = ({
  activeStage,
  statuses,
  onChange,
}: {
  activeStage: ImportStage;
  statuses: Record<ImportStage, ImportStageStatus>;
  onChange: (stage: ImportStage) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.75,
        backgroundColor: "grey.50",
        borderRadius: 1,
      }}
    >
      <Box
        role="tablist"
        aria-label={t("externalRegister.import.header.stages.ariaLabel")}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(5, minmax(0, 1fr))",
          },
          gap: 0.75,
        }}
      >
        {IMPORT_STAGES.map((stage, index) => {
          const selected = activeStage === stage.value;
          const status = statuses[stage.value];

          return (
            <Button
              key={stage.value}
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(stage.value)}
              variant="text"
              sx={{
                justifyContent: "flex-start",
                minHeight: 58,
                px: 1.25,
                py: 1,
                borderRadius: 0.75,
                border: "1px solid",
                borderColor: selected ? "primary.main" : "transparent",
                backgroundColor: selected ? "background.paper" : "transparent",
                color: selected ? "text.primary" : "text.secondary",
                boxShadow: selected ? 1 : "none",
                textAlign: "left",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "background.paper",
                  borderColor: selected ? "primary.main" : "divider",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
                <StageNumber status={status} selected={selected} value={index + 1} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    {t(`externalRegister.import.stages.labels.${stage.value}`)}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: "block",
                      color: selected ? "text.secondary" : "text.disabled",
                      lineHeight: 1.25,
                    }}
                  >
                    {getStageStatusLabel(status, t)}
                  </Typography>
                </Box>
              </Stack>
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
};

const getStageStatusLabel = (status: ImportStageStatus, t: (key: string) => string): string => {
  if (status === "complete") return t("common.status.completed");
  if (status === "inProgress") return t("common.status.inProgress");
  return t("common.status.waiting");
};

const StageNumber = ({
  status,
  selected,
  value,
}: {
  status: ImportStageStatus;
  selected: boolean;
  value: number;
}) => {
  const backgroundColor = status === "complete"
    ? "success.main"
    : status === "inProgress"
      ? "primary.main"
      : selected
        ? "grey.700"
        : "grey.300";
  const color = status === "waiting" && !selected ? "text.secondary" : "common.white";

  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        backgroundColor,
        color,
        fontSize: 13,
        fontWeight: 800,
      }}
    >
      {status === "complete" ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : value}
    </Box>
  );
};
